/**
 * Слова алгоритма реагирования `engage-v1` (AN-1): категории комментария,
 * язык, предложение «что и почему», ручная очередь «Нужен человек».
 * Простым языком — их читает человек, который отвечает на комментарии.
 */
/** Имя алгоритма v1 в движке. */
export const ENGAGE_V1_POLICY = 'engage-v1';
/** «Нет — только вручную»: ничего не предлагает. */
export const NOOP_POLICY = 'noop';
/** Подписи алгоритмов в списке «Алгоритм» (настройки реагирования). */
export const POLICY_LABEL_RU = {
    [ENGAGE_V1_POLICY]: 'Алгоритм v1 (рекомендуется)',
    [NOOP_POLICY]: 'Нет — только вручную',
};
/** Короткое имя алгоритма — в журнале («Кто: Алгоритм v1»). */
export const POLICY_SHORT_LABEL_RU = {
    [ENGAGE_V1_POLICY]: 'Алгоритм v1',
    [NOOP_POLICY]: 'Нет — только вручную',
};
/**
 * Что делает алгоритм — простыми словами (AN-1 §5, §8, выводы 6–7). Рядом с
 * выбором алгоритма и в карточке настроек аккаунта.
 */
export const POLICY_DESCRIPTION_RU = {
    [ENGAGE_V1_POLICY]: [
        'Ставит сердечко примерно половине хороших комментариев (похвала, отметки друзей, сердечки) — не всем подряд, чтобы не выглядеть ботом.',
        'Иногда отвечает — на похвалу, просьбы «сделайте ещё» и отметки друзей — короткой фразой из заготовок на языке комментария (португальский, испанский, английский).',
        'Скрывает спам с явной ссылкой, почтой или телефоном (скрытие можно отменить).',
        'Остальное отдаёт человеку — «Комментарии» → «Ручная очередь»: вопросы к каналу, негатив, оскорбления, спам без явной ссылки.',
        'Комментарии старше 48 часов не трогает. В режиме «Вручную» всё это — предложения на подтверждение, в «Авто» выполняется само (кроме ручной очереди).',
    ],
    [NOOP_POLICY]: [
        'Алгоритм ничего не предлагает: все комментарии видны в журнале, сердечки, ответы и скрытие — только вручную.',
    ],
};
/** Описание алгоритма списком фраз; незнакомый — описание движка (или ничего). */
export function policyDescription(name, engineText) {
    return POLICY_DESCRIPTION_RU[name] ?? (engineText ? [engineText] : []);
}
/** Категории комментария по-русски (фильтр и колонка «Категория»). */
export const SOCIAL_COMMENT_CATEGORY_LABEL_RU = {
    praise: 'похвала',
    request: 'просьба',
    question: 'вопрос',
    tag_text: 'отметка друга с текстом',
    tag_emoji: 'отметка друга с эмодзи',
    tag_only: 'отметка друга',
    relatable: '«это мы»',
    laugh: 'шутка, смех',
    emoji_love: 'эмодзи: сердечки',
    emoji_laugh: 'эмодзи: смех',
    emoji_other: 'эмодзи: другое',
    emoji_negative: 'эмодзи: недовольство',
    sad: 'грусть',
    plot_talk: 'про сюжет',
    personal_message: 'личное (не нам)',
    fragment: 'пара слов',
    other: 'другое',
    empty: 'стикер / пусто',
    negative: 'негатив',
    toxic: 'токсичное',
    nsfw: '18+',
    spam: 'спам',
};
/** Порядок категорий в фильтре: частые и понятные — сверху, защитные — отдельно внизу. */
export const SOCIAL_COMMENT_CATEGORY_ORDER = [
    'praise',
    'tag_only',
    'tag_emoji',
    'tag_text',
    'emoji_love',
    'emoji_laugh',
    'emoji_other',
    'question',
    'request',
    'laugh',
    'relatable',
    'plot_talk',
    'sad',
    'personal_message',
    'fragment',
    'other',
    'empty',
    'negative',
    'emoji_negative',
    'toxic',
    'nsfw',
    'spam',
];
/** Категории, которые алгоритм всегда отдаёт человеку (AN-1 §5). */
export const HUMAN_CATEGORIES = new Set([
    'question',
    'negative',
    'toxic',
    'nsfw',
    'spam',
]);
const isKnownCategory = (c) => c in SOCIAL_COMMENT_CATEGORY_LABEL_RU;
/** Категория словами; незнакомая (новая у движка) — как есть; null — алгоритм не смотрел. */
export function categoryLabel(raw) {
    if (raw === null)
        return null;
    return isKnownCategory(raw) ? SOCIAL_COMMENT_CATEGORY_LABEL_RU[raw] : raw;
}
/** Цвет бейджа категории: защитные — тревожные, похвала/«это мы» — зелёные. */
export function categoryVariant(raw) {
    if (raw === 'toxic' || raw === 'nsfw' || raw === 'spam')
        return 'danger';
    if (raw === 'negative' || raw === 'emoji_negative')
        return 'warning';
    if (raw === 'question' || raw === 'request')
        return 'info';
    if (raw === 'praise' || raw === 'relatable' || raw === 'laugh')
        return 'success';
    return 'neutral';
}
/** Язык комментария по-русски (фильтр и подпись под категорией). */
export const SOCIAL_COMMENT_LANG_LABEL_RU = {
    pt: 'португальский',
    es: 'испанский',
    en: 'английский',
    tie: 'португальский или испанский',
    none: 'без слов',
    unk: 'не определён',
};
const OTHER_LANG_RU = {
    ru: 'русский',
    ko: 'корейский',
    ar: 'арабский',
    fa: 'персидский',
    he: 'иврит',
    th: 'тайский',
    ja: 'японский',
    zh: 'китайский',
    hi: 'хинди',
    el: 'греческий',
    it: 'итальянский',
    fr: 'французский',
    de: 'немецкий',
    id: 'индонезийский',
    tr: 'турецкий',
    tl: 'тагальский',
    pl: 'польский',
    nl: 'нидерландский',
    vi: 'вьетнамский',
    ro: 'румынский',
};
/** Язык словами; незнакомый код — как есть; null — не определяли. */
export function langLabel(raw) {
    if (raw === null)
        return null;
    return (SOCIAL_COMMENT_LANG_LABEL_RU[raw] ??
        OTHER_LANG_RU[raw] ??
        raw);
}
/** Что предлагает алгоритм (`suggestion.kind`). */
const SUGGESTION_KIND_RU = {
    reply: 'ответить',
    like: 'поставить сердечко',
    hide: 'скрыть',
    delete: 'удалить',
    review: 'посмотреть самому',
};
/** Почему (`suggestion.reason` или категория комментария). */
const SUGGESTION_REASON_RU = {
    question: 'это вопрос к каналу — отвечать должен человек',
    mentions_us: 'комментарий упоминает наш аккаунт — отвечать должен человек',
    negative: 'похоже на негатив — посмотрите сами, автоматика на негатив не реагирует',
    toxic: 'похоже на оскорбление — посмотрите сами: алгоритм ошибается примерно в половине случаев (часто это шутка «это мы» с матом)',
    nsfw: 'похоже на 18+ — посмотрите сами',
    spam: 'похоже на спам без явной ссылки (промо-фраза или адрес сайта без http) — скройте, если это действительно спам',
    spam_link: 'спам с явной ссылкой, почтой или телефоном',
    praise: 'это похвала',
    request: 'это просьба «сделайте ещё»',
    tag_text: 'это отметка друга с фразой',
    tag_emoji: 'это отметка друга с эмодзи',
    tag_only: 'это отметка друга',
    relatable: 'это «это мы»',
    laugh: 'это шутка или смех',
    emoji_love: 'это сердечки-эмодзи',
};
/**
 * Причина решения алгоритма словами (категория, `spam_link`, причина ручной
 * очереди); незнакомая — null (решает вызывающий).
 */
export function policyReasonPhrase(reason) {
    return (SUGGESTION_REASON_RU[reason] ??
        (isKnownCategory(reason) ? `это ${categoryLabel(reason)}` : null));
}
export function describeSuggestion(a, category = null) {
    if (a.requiresApproval !== true && a.source !== 'policy')
        return null;
    return suggestionText(a.kind, a.reason ?? category);
}
/**
 * Своя фраза к известной причине важнее фразы движка (`reasonText`): в ней
 * то, что человеку нужно знать (например, что «токсичное» — наполовину
 * шутки). Незнакомая причина — фраза движка, иначе имя категории или код.
 */
function suggestionText(kind, reason, engineText = null) {
    const what = SUGGESTION_KIND_RU[kind] ?? kind;
    const why = reason !== null
        ? (SUGGESTION_REASON_RU[reason] ??
            engineText ??
            (isKnownCategory(reason) ? `это ${categoryLabel(reason)}` : reason))
        : engineText;
    return { what, why };
}
/**
 * Предложение человеку по комментарию в целом (ручная очередь): «ответить —
 * это вопрос к каналу». null — алгоритм ничего человеку не отдавал.
 */
export function describeCommentSuggestion(c) {
    if (c.suggestion === null)
        return null;
    return suggestionText(c.suggestion.kind, c.suggestion.reason ?? c.category, c.suggestion.reasonText);
}
/** Предложение человеку ещё ждёт (не отмечено «просмотрен»). */
export function humanSuggestionPending(c) {
    return c.suggestion !== null && c.suggestion.pending !== false;
}
/** Действие, которое алгоритм отдал человеку («Нужен человек»). */
export function needsHumanAction(a) {
    return a.requiresApproval === true;
}
/** Комментарий в ручной очереди: есть предложение, отданное человеку. */
export function needsHuman(c) {
    return humanSuggestionPending(c) || c.pending.some(needsHumanAction);
}
/** Бейдж ручной очереди. */
export const NEEDS_HUMAN_LABEL = 'нужен человек';
/**
 * Комментарий защитной категории без предложения (алгоритм отдал его
 * человеку без действия, `suggest=review`): почему алгоритм ничего не сделал.
 */
export function reviewHint(c) {
    if (c.category === null ||
        !HUMAN_CATEGORIES.has(c.category) ||
        c.pending.length > 0 ||
        c.suggestion !== null) {
        return null;
    }
    const why = SUGGESTION_REASON_RU[c.category];
    return why ? `Алгоритм ничего не делает сам: ${why}.` : null;
}
//# sourceMappingURL=policy-text.js.map